import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, Send, RefreshCw, Filter, Loader2, ShieldOff, CheckCircle2, AlertOctagon, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { api, apiErrorText } from "@/lib/api";
import {
  VLKPageHeader, VLKCard, VLKKPICard, VLKButton, VLKSelect, VLKInput,
  VLKLabel, VLKAlertDialog, toast, VLKEmptyState,
} from "@/components/vlk";

const STATUS_STYLES = {
  QUEUED:            "text-info border-info/30 bg-info/5",
  RETRY_SCHEDULED:   "text-warning border-warning/30 bg-warning/5",
  SENT:              "text-success border-success/30 bg-success/5",
  FAILED:            "text-destructive border-destructive/30 bg-destructive/5",
  SUPPRESSED:        "text-muted-foreground border-border",
};

const CATEGORIES = ["digest", "security_alert", "case_assigned", "sla_escalation", "onboarding", "credential", "recovery"];

export default function EmailOps() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ status: "", category: "" });
  const [selected, setSelected] = useState(null);
  const [showPrefs, setShowPrefs] = useState(false);

  const { data: status } = useQuery({ queryKey: ["email", "status"], queryFn: () => api.get("/email/status").then(r => r.data) });
  const { data: stats } = useQuery({ queryKey: ["email", "stats"], queryFn: () => api.get("/email/queue/stats").then(r => r.data), refetchInterval: 5000 });
  const { data: queue, refetch } = useQuery({
    queryKey: ["email", "queue", filters],
    queryFn: () => api.get("/email/queue", { params: { status: filters.status || undefined, category: filters.category || undefined, limit: 200 } }).then(r => r.data),
    refetchInterval: 5000,
  });

  const retry = useMutation({
    mutationFn: (id) => api.post(`/email/queue/${id}/retry`).then(r => r.data),
    onSuccess: () => { toast.success("Requeued for delivery"); qc.invalidateQueries({ queryKey: ["email"] }); },
    onError: (e) => toast.error("Retry failed", { description: apiErrorText(e) }),
  });

  return (
    <div data-testid="email-ops-page">
      <VLKPageHeader
        title="Email operations"
        description="Provider abstraction with queued asynchronous delivery, retry backoff, bounce logging, and per-recipient preferences. Every terminal event is audit-logged."
        actions={
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-sm border border-border bg-card text-[10px] uppercase tracking-widest font-mono">
              <span className={cn("h-2 w-2 rounded-full", status?.mocked ? "bg-warning animate-pulse" : "bg-success")} />
              <span>{status?.provider || "…"}</span>
              {status?.mocked && <span className="text-warning">MOCKED</span>}
            </div>
            <VLKButton variant="outline" size="md" onClick={() => setShowPrefs(true)} data-testid="email-prefs-btn"><ShieldOff className="h-4 w-4" /> Preferences</VLKButton>
            <VLKButton variant="outline" size="md" onClick={() => refetch()} data-testid="email-refresh"><RefreshCw className="h-4 w-4" /></VLKButton>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <VLKKPICard testId="email-kpi-queued" label="Queued" value={stats?.queued ?? "—"} icon={Clock} tone="info" />
        <VLKKPICard testId="email-kpi-retry" label="Retry scheduled" value={stats?.retry_scheduled ?? "—"} icon={RefreshCw} tone="warning" />
        <VLKKPICard testId="email-kpi-sent" label="Sent" value={(stats?.sent ?? 0).toLocaleString()} icon={CheckCircle2} tone="success" />
        <VLKKPICard testId="email-kpi-failed" label="Failed" value={stats?.failed ?? "—"} icon={AlertOctagon} tone="danger" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-4">
        <VLKCard>
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <VLKSelect data-testid="email-status-filter" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="max-w-[180px]">
              <option value="">All status</option>
              {Object.keys(STATUS_STYLES).map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </VLKSelect>
            <VLKSelect data-testid="email-category-filter" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className="max-w-[180px]">
              <option value="">All categories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
            </VLKSelect>
          </div>
          <div className="overflow-x-auto max-h-[calc(100vh-320px)]">
            {(queue?.items || []).length === 0 ? (
              <VLKEmptyState icon={Mail} title="No messages in this view" description="Try broadening filters or queue a delivery." />
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-muted/40 border-b border-border sticky top-0">
                  <tr>
                    {["Time", "Template", "Category", "To", "Status", "Attempts", ""].map((h) => (
                      <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(queue?.items || []).map((m) => (
                    <tr key={m.message_id} data-testid={`email-row-${m.message_id}`}
                        onClick={() => setSelected(m)}
                        className={cn("border-b border-border last:border-0 hover:bg-secondary/50 transition-colors cursor-pointer font-mono text-xs",
                                       selected?.message_id === m.message_id && "bg-secondary/70")}>
                      <td className="px-3 py-2 text-muted-foreground tabular">{new Date(m.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2 font-medium normal-case">{m.template} v{m.template_version}</td>
                      <td className="px-3 py-2 text-muted-foreground uppercase tracking-widest text-[10px]">{m.category}</td>
                      <td className="px-3 py-2 text-muted-foreground truncate max-w-[200px]">{(m.to || []).join(", ")}</td>
                      <td className="px-3 py-2">
                        <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 border rounded-sm text-[10px] uppercase tracking-wider", STATUS_STYLES[m.status])}>{m.status.replace("_", " ")}</span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground tabular">{m.attempts}/{m.max_attempts}</td>
                      <td className="px-3 py-2 text-right">
                        {(m.status === "FAILED" || m.status === "SENT") && (
                          <VLKButton variant="ghost" size="sm" data-testid={`email-retry-${m.message_id}`} onClick={(e) => { e.stopPropagation(); retry.mutate(m.message_id); }}>
                            <RefreshCw className="h-3 w-3" />
                          </VLKButton>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </VLKCard>

        <VLKCard className="min-h-[400px]">
          <div className="px-4 py-3 border-b border-border">
            <div className="font-display text-sm">Inspector</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">Select a message to inspect metadata & delivery history.</div>
          </div>
          {selected ? (
            <div className="p-4 space-y-2 text-xs">
              <Row label="Message">{selected.message_id}</Row>
              <Row label="Subject"><span className="font-mono">{selected.subject}</span></Row>
              <Row label="From">{selected.from_addr}</Row>
              <Row label="To">{(selected.to || []).join(", ")}</Row>
              <Row label="Category">{selected.category}</Row>
              <Row label="Correlation">{selected.correlation_id}</Row>
              <Row label="Attempts">{selected.attempts}/{selected.max_attempts}</Row>
              <Row label="Provider">{selected.provider || "—"}</Row>
              {selected.last_error && <Row label="Last error"><span className="text-destructive">{selected.last_error}</span></Row>}
              {selected.sent_at && <Row label="Sent at">{new Date(selected.sent_at).toLocaleString()}</Row>}
              {selected.failed_at && <Row label="Failed at">{new Date(selected.failed_at).toLocaleString()}</Row>}
              <div className="pt-2 border-t border-border">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">History</div>
                <ul className="mt-1 space-y-1 max-h-40 overflow-auto">
                  {(selected.history || []).map((h, i) => (
                    <li key={`${selected.message_id}-hist-${h.at || i}-${h.kind}`} className="text-[11px] font-mono">
                      <span className="text-muted-foreground">{new Date(h.at).toLocaleString()}</span> — {h.kind}
                      {h.detail && <span className="text-muted-foreground"> · {h.detail}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <div className="p-6 text-sm text-muted-foreground">Select a message to see delivery timeline.</div>
          )}
        </VLKCard>
      </div>

      {showPrefs && <PreferencesDialog onClose={() => setShowPrefs(false)} />}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[100px_1fr] gap-3 items-start">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground pt-0.5">{label}</div>
      <div className="break-all">{children}</div>
    </div>
  );
}

function PreferencesDialog({ onClose }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["email", "prefs"], queryFn: () => api.get("/email/preferences").then(r => r.data) });
  const [form, setForm] = useState({ recipient: "", suppressed_categories: [] });

  const upsert = useMutation({
    mutationFn: () => api.post("/email/preferences", form).then(r => r.data),
    onSuccess: () => { toast.success("Preference saved"); qc.invalidateQueries({ queryKey: ["email", "prefs"] }); setForm({ recipient: "", suppressed_categories: [] }); },
    onError: (e) => toast.error("Save failed", { description: apiErrorText(e) }),
  });

  const toggle = (c) => setForm((f) => f.suppressed_categories.includes(c)
    ? { ...f, suppressed_categories: f.suppressed_categories.filter((x) => x !== c) }
    : { ...f, suppressed_categories: [...f.suppressed_categories, c] });

  return (
    <VLKAlertDialog open onOpenChange={(v) => !v && onClose()} title="Email preferences"
      actions={<VLKButton variant="ghost" onClick={onClose}>Close</VLKButton>}>
      <div className="space-y-4">
        <div>
          <VLKLabel>Recipient</VLKLabel>
          <VLKInput data-testid="email-pref-recipient" value={form.recipient} onChange={(e) => setForm({ ...form, recipient: e.target.value })} placeholder="user@example.com" />
        </div>
        <div>
          <VLKLabel>Suppress categories</VLKLabel>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {CATEGORIES.map((c) => (
              <button key={c} type="button" onClick={() => toggle(c)}
                      className={cn("px-2 py-1 border rounded-sm text-[10px] uppercase tracking-wider",
                                     form.suppressed_categories.includes(c) ? "border-destructive text-destructive bg-destructive/5" : "border-border text-muted-foreground hover:bg-secondary")}>
                {c}
              </button>
            ))}
          </div>
        </div>
        <VLKButton data-testid="email-pref-save" loading={upsert.isPending} disabled={!form.recipient} onClick={() => upsert.mutate()}>Save preference</VLKButton>
        <div className="pt-3 border-t border-border">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Existing preferences</div>
          {(data?.items || []).length === 0 ? (
            <div className="text-xs text-muted-foreground">No suppressions configured.</div>
          ) : (
            <ul className="space-y-1 text-xs font-mono max-h-40 overflow-auto">
              {(data?.items || []).map((p) => (
                <li key={p.recipient} className="flex justify-between gap-2">
                  <span>{p.recipient}</span>
                  <span className="text-muted-foreground">{(p.suppressed_categories || []).join(", ") || "none"}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </VLKAlertDialog>
  );
}
